'use client';

import { useEffect, useMemo, useState } from 'react';

import { DEFAULT_COUNTRY_CODE } from '@/lib/constants/countries';
import { getAvailableShippingMethods } from '@/lib/functions/getAvailableShippingMethods';
import { resolveCheckoutShippingDefaults } from '@/lib/functions/resolveCheckoutShippingDefaults';
import { shouldReplaceAvailableShippingMethods } from '@/lib/functions/preferedShippingMethods';

import type { OrderPayload } from '@/lib/types/orderPayload';
import type { ShippingMethod } from '@/lib/types/shippingMethod';

export function useCheckoutShippingMethods(
  orderData: OrderPayload | null | undefined,
) {
  const [displayedShippingMethods, setDisplayedShippingMethods] = useState<ShippingMethod[]>([]);
  const [shippingMethodsLoading, setShippingMethodsLoading] = useState(true);
  const [hasLoadedShippingMethods, setHasLoadedShippingMethods] = useState(false);

  const orderToken = orderData?.token;
  const primaryShipmentId = orderData?.shipments?.[0]?.id;
  const orderIsAddressed = Boolean(
    orderData?.states?.checkout && orderData.states.checkout !== 'cart',
  );

  const shippingDefaults = useMemo(
    () => resolveCheckoutShippingDefaults(orderData, null),
    [orderData],
  );

  const shippingCountryCode = (shippingDefaults.countryCode ?? DEFAULT_COUNTRY_CODE).toUpperCase();
  const shippingMethodsAddressKey = orderToken
    ? [
      orderToken,
      shippingDefaults.street,
      shippingDefaults.street2,
      shippingDefaults.city,
      shippingDefaults.provinceCode,
      shippingDefaults.postcode,
      shippingCountryCode,
    ].join('|')
    : '';

  useEffect(() => {
    const fetchShippingMethods = async () => {
      if (typeof window === 'undefined' || !orderToken || !primaryShipmentId || !orderIsAddressed) {
        setShippingMethodsLoading(false);
        return;
      }

      setShippingMethodsLoading(true);

      try {
        const shippingMethods = await getAvailableShippingMethods(orderToken, primaryShipmentId);
        const nextMethods = shippingMethods || [];

        setDisplayedShippingMethods((current) => {
          const isFirstLoad = current.length === 0;
          return shouldReplaceAvailableShippingMethods(current, nextMethods, isFirstLoad)
            ? nextMethods
            : current;
        });
        setHasLoadedShippingMethods(true);
      } finally {
        setShippingMethodsLoading(false);
      }
    };

    void fetchShippingMethods();
  }, [
    orderIsAddressed,
    orderToken,
    primaryShipmentId,
    shippingMethodsAddressKey,
  ]);

  return {
    displayedShippingMethods,
    shippingMethodsLoading,
    hasLoadedShippingMethods,
  };
}
